import { friendlyError, type OAuthProvider, useAuth } from '@hobbytools/platform';
import { Banner, Button, Divider, TextField } from '@hobbytools/ui';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useNext } from '../lib/next';
import { AuthLayout, linkClass } from './AuthLayout';

const providerLabel: Record<OAuthProvider, string> = { discord: 'Discord', google: 'Google', apple: 'Apple' };

export function SignIn() {
  const { user, isLoading, signIn, signInWithOAuth, providers } = useAuth();
  const { next, go, query } = useNext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isLoading && user) go();
  }, [isLoading, user, go]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      lead="Pick up where your campaign left off."
      footer={
        <>
          New here?{' '}
          <Link className={linkClass} to={`/sign-up${query}`}>
            Create an account
          </Link>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {providers.length > 0 ? (
        <>
          <div className="flex flex-col gap-2">
            {providers.map((p) => (
              <Button
                key={p}
                variant={p === 'discord' ? 'primary' : 'secondary'}
                onPress={() => signInWithOAuth(p, next)}
              >
                Continue with {providerLabel[p]}
              </Button>
            ))}
          </div>
          <Divider spacing="none" />
        </>
      ) : null}
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          isRequired
          value={email}
          onChange={setEmail}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          isRequired
          value={password}
          onChange={setPassword}
        />
        <Button type="submit" isDisabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
      <Link className={`${linkClass} text-sm`} to="/reset-password">
        Forgot your password?
      </Link>
    </AuthLayout>
  );
}
